import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { useMemo, useState } from "react";

import { useOrderMarkets } from "@web-app/api";
import { Icon } from "@web-app/components";

interface RegionFilterValueProps {
  onChange: (values: string[]) => void;
  values: string[];
}

export function RegionFilterValue({
  onChange,
  values,
}: RegionFilterValueProps) {
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [temporaryValues, setTemporaryValues] = useState<string[]>(values);

  const { data: statesMap, isSuccess } = useOrderMarkets({
    enabled: isOpen,
  });

  const sortedStates = useMemo(() => {
    if (!isOpen || !statesMap) return [];

    const statesArray = Array.from(statesMap.values());
    const filtered = searchText
      ? statesArray.filter((state) =>
          state.name.toLowerCase().includes(searchText.toLowerCase()),
        )
      : statesArray;

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [isOpen, statesMap, searchText]);

  const handleOpen = () => {
    setIsOpen(true);
    setTemporaryValues(values);
    setSearchText("");
  };

  const handleToggleState = (stateCode: string) => {
    setTemporaryValues((prev) =>
      prev.includes(stateCode)
        ? prev.filter((v) => v !== stateCode)
        : [...prev, stateCode],
    );
  };

  const handleApply = (close: () => void) => {
    onChange(temporaryValues);
    close();
  };

  const handleCancel = (close: () => void) => {
    setTemporaryValues(values);
    setSearchText("");
    close();
  };

  const displayText =
    values.length === 0
      ? "Select regions..."
      : values.length <= 2
        ? values.join(", ")
        : `${values.slice(0, 2).join(", ")} +${values.length - 2}`;

  return (
    <Popover className="relative">
      <PopoverButton
        onClick={handleOpen}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md text-left bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
      >
        <span className={values.length === 0 ? "text-gray-400" : ""}>
          {displayText}
        </span>
        <Icon name="fasChevronDown" className="h-3 w-3 text-gray-400 ml-1" />
      </PopoverButton>
      <Transition
        as="div"
        enter="transition duration-100 ease-out"
        enterFrom="transform scale-95 opacity-0"
        enterTo="transform scale-100 opacity-100"
        leave="transition duration-75 ease-out"
        leaveFrom="transform scale-100 opacity-100"
        leaveTo="transform scale-95 opacity-0"
        className="absolute right-0 top-[42px] z-20 w-72 rounded-md bg-white shadow-xl border border-gray-200"
        afterLeave={() => setIsOpen(false)}
      >
        <PopoverPanel>
          {({ close }) => (
            <div className="p-3">
              <input
                type="search"
                placeholder="Search regions..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              />
              {!isSuccess && (
                <div className="py-4 text-xs text-gray-500 text-center">
                  Loading regions...
                </div>
              )}
              {isSuccess && (
                <div className="flex flex-col gap-0.5 overflow-y-auto max-h-[250px] mb-2">
                  {sortedStates.length > 0 ? (
                    sortedStates.map((state) => (
                      <label
                        key={state.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={temporaryValues.includes(state.stateCode)}
                          onChange={() => handleToggleState(state.stateCode)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="truncate">{state.name}</span>
                      </label>
                    ))
                  ) : (
                    <div className="py-4 text-xs text-gray-500 text-center">
                      {searchText
                        ? "No regions match your search."
                        : "No regions available."}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <button
                  onClick={() => handleCancel(close)}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleApply(close)}
                  className="px-3 py-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                  type="button"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
